/**
 * Account read — the adapter between what the API actually holds and what the
 * design asks for.
 *
 * WHY THIS FILE EXISTS AS ITS OWN LAYER
 * -------------------------------------
 * The design (performance-portfolio.html) is built on a premise its own note
 * states plainly: "delta is the frame, not the level — every number on the page
 * carries its prior-period value". Our stored metrics cannot honour that. The
 * grid's own SELECT says so in as many words:
 *
 *     campaign_grid_metrics is keyed (campaign_id, platform) with no date
 *     dimension, so the numbers in a filtered row are still all-time
 *     — routes/v2/campaignGrid.js, and the response echoes metricsWindowed:false
 *
 * So there is exactly one row per campaign, overwritten by each pull. No daily
 * series, no previous period, no impression share, no search terms, no
 * device/geo/hour grain.
 *
 * Rather than let that discrepancy leak into the view as zeros or invented
 * numbers, every derivation lives here and returns ONE of two things: a real
 * value, or an `unavailable(reason)` marker naming what is missing. The view
 * renders the second as the design's own "not wired" state. The rule this
 * enforces is that a number on this page is always a measurement — never a
 * placeholder that happens to look like one.
 *
 * When a windowed/daily metrics source lands, the components do not change:
 * these functions start returning values instead of markers.
 */

/** Marker for "we do not hold this", distinguishable from a real 0. */
export function unavailable(reason, needs) {
  return { unavailable: true, reason, needs: needs || null };
}

export function isUnavailable(v) {
  return Boolean(v && v.unavailable);
}

/**
 * The reasons, written once so the view and the tests quote the same text.
 * Each names the missing SOURCE, not just the missing number — "no daily series"
 * is actionable, "no data" is not.
 */
export const NEEDS = {
  series: 'a daily metrics series (campaign_grid_metrics holds one lifetime row per campaign, with no date dimension)',
  prior: 'a previous-period comparison (the cached metrics carry no date dimension, so there is nothing to compare against)',
  impressionShare: 'impression-share fields (search_impression_share, search_budget_lost_impression_share) — pulled today only inside an optimizer run, never persisted',
  outcomes: 'change-outcome classification (the changelog records changes, not whether they landed)',
  oneCurrency: 'a single currency across the account (spend is stored in each advertiser\'s own currency and is never converted, so amounts in different currencies cannot be added)',
};

/**
 * Postgres NUMERIC and JSON-text columns both arrive as strings, and
 * `conversions` is deliberately served as TEXT so one bad producer value cannot
 * error the whole grid (see the SELECT's own comment). Everything therefore gets
 * coerced here rather than at each use site.
 *
 * Returns null — never 0 — for anything unparseable. A campaign that has never
 * been measured and a campaign that delivered nothing are different claims, and
 * collapsing them to 0 would report the first as the second.
 */
export function num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Sum that stays null when NOTHING contributed, but ignores individual nulls. */
function sum(rows, pick) {
  let total = null;
  for (const r of rows) {
    const v = num(pick(r));
    if (v === null) continue;
    total = (total === null ? 0 : total) + v;
  }
  return total;
}

/** a / b, guarding both the null and the divide-by-zero case. */
function ratio(a, b) {
  if (a === null || b === null || b === 0) return null;
  return a / b;
}

/**
 * The window the cached numbers actually cover.
 *
 * The design's header reads "Last 30 days vs previous 30 days". We must not
 * print that unless it is true, so the header renders THIS instead: whatever
 * `metrics_window` recorded, or an explicit "lifetime totals" when the pull
 * carried no window (which is what NULL means, per the SELECT).
 *
 * Campaigns can disagree — they are pulled per customer — so a mixed set is
 * reported as mixed rather than silently taking the first.
 */
export function metricsWindow(campaigns) {
  const seen = new Set();
  for (const c of campaigns) {
    const w = c.metrics_window || c.metricsWindow || null;
    seen.add(w && w.start_date && w.end_date ? `${w.start_date}|${w.end_date}` : 'lifetime');
  }
  if (seen.size === 0) return { kind: 'none', label: 'no metrics pulled yet' };
  if (seen.size > 1) return { kind: 'mixed', label: 'mixed windows across campaigns' };
  const only = [...seen][0];
  if (only === 'lifetime') return { kind: 'lifetime', label: 'lifetime totals' };
  const [start, end] = only.split('|');
  return { kind: 'window', start, end, label: `${start} → ${end}` };
}

/**
 * The grid rows, restated for a picked date range.
 *
 * WHAT THIS IS DOING. The grid's own numbers carry no date dimension — one row
 * per (campaign, platform), whatever window its last pull asked for. The daily
 * rows DO exist, in that pull's artifact, and /performance/windowed-metrics
 * rolls them up per campaign for a range. This swaps the range's numbers in so
 * every derivation downstream — totals, tiles, the table, top spenders, spending
 * -not-converting — is about the range without any of them knowing about ranges.
 *
 * A campaign the roll-up does not cover gets NULLS, not its lifetime numbers.
 * Nulls are this file's "never measured", so it drops out of the sums and renders
 * as "—", and the caller reports how many dropped. Leaving the lifetime value in
 * place under a range label is the one outcome that must not happen: it reads as
 * a measurement of the range and is off by however long the campaign has run.
 * The uncovered set is real and normal — Google Ads and DV360 pulls emit daily rows,
 * so every LinkedIn, Meta and DV360 campaign lands here.
 */
export function applyWindow(campaigns, windowed) {
  if (!windowed) return { campaigns, covered: campaigns.length, uncovered: [] };
  const byId = windowed.campaigns || {};
  const uncovered = [];
  const out = campaigns.map((c) => {
    const w = byId[c.id];
    if (!w) {
      uncovered.push(c);
      return {
        ...c,
        spend: null,
        impressions: null,
        clicks: null,
        conversions: null,
        ctr: null,
        windowed: false,
      };
    }
    return {
      ...c,
      spend: w.cost,
      impressions: w.impressions,
      clicks: w.clicks,
      conversions: w.conversions,
      // Recomputed by the server from the range's summed parts, so it is the
      // range's CTR and not the stored lifetime one.
      ctr: w.ctr,
      windowed: true,
      windowDays: w.days,
    };
  });
  return { campaigns: out, covered: out.length - uncovered.length, uncovered };
}

/**
 * The distinct currencies the campaigns CONTRIBUTING SPEND report in.
 *
 * Only campaigns with a spend figure count: one with no delivery adds nothing to
 * the total, so its currency cannot corrupt it, and letting it do so would hide
 * a perfectly good number. Campaigns whose currency is unknown are not counted
 * as a second currency either — an unknown is not a conflict, and treating it as
 * one would blank the tile for every account whose artifacts predate the
 * currency column.
 */
export function spendCurrencies(campaigns) {
  return [...new Set((campaigns || [])
    // Coerced through num() for the same reason sum() does: Postgres NUMERIC
    // arrives as a string, so a raw Number.isFinite check would find NOTHING
    // contributing and never detect a conflict at all.
    .filter((c) => num(c?.spend) !== null)
    .map((c) => c?.currency_code)
    .filter(Boolean))].sort();
}

/**
 * Account-wide roll-up of the per-campaign rows.
 *
 * MONEY IS NOT SUMMED ACROSS CURRENCIES. Spend is stored in each advertiser's
 * own currency — DV360's METRIC_REVENUE_ADVERTISER is advertiser-currency by
 * definition, and Google Ads' cost_micros is customer-currency — and nothing in
 * this pipeline converts. The old roll-up added every campaign's spend and then
 * labelled the result with `campaigns.map(c => c.currency_code).find(Boolean)`:
 * the FIRST campaign's currency, or USD when none had one. On an account whose
 * advertisers bill in different currencies that produced a number that is not a
 * quantity of anything, printed with a definite currency symbol — the most
 * confident possible way to be wrong.
 *
 * So when more than one currency is in play the money figures report themselves
 * unavailable and the view renders them as "—" with the reason. The counts are
 * untouched: impressions, clicks, conversions and CTR are currency-free and stay
 * exactly as true as they were.
 */
export function totals(campaigns) {
  const currencies = spendCurrencies(campaigns);
  const mixed = currencies.length > 1;
  const cost = sum(campaigns, (c) => c.spend);
  const impressions = sum(campaigns, (c) => c.impressions);
  const clicks = sum(campaigns, (c) => c.clicks);
  const conversions = sum(campaigns, (c) => c.conversions);
  const noSum = () => unavailable(
    `mixed currencies across this account (${currencies.join(', ')}) — amounts are not converted`,
    NEEDS.oneCurrency,
  );
  return {
    cost: mixed ? noSum() : cost,
    impressions,
    clicks,
    conversions,
    // Recomputed from the summed parts rather than averaged from per-campaign
    // CTRs — a mean of ratios is not the ratio of the sums, and the second is
    // the one that is true of the account.
    ctr: ratio(clicks, impressions),
    // Both derive from `cost`, so both inherit its currency problem.
    cpa: mixed ? noSum() : ratio(cost, conversions),
    cpc: mixed ? noSum() : ratio(cost, clicks),
    currency: currencies.length === 1 ? currencies[0] : null,
    currencies,
    mixedCurrency: mixed,
  };
}

/**
 * The four scorecard tiles backed by stored account metrics.
 * Every tile's delta is unavailable because there is no previous period to
 * compare with.
 */
export function scorecard(t) {
  const noPrior = unavailable('no previous period', NEEDS.prior);
  const tile = (label, value, format) => ({ label, value, format, delta: noPrior });
  return [
    tile('Cost', t.cost, 'currency'),
    tile('Conversions', t.conversions, 'int'),
    tile('Cost / conv.', t.cpa, 'currency2'),
    tile('CTR', t.ctr, 'pct'),
  ];
}

/* ── Competitive ───────────────────────────────────────────────────────────
 *
 * WHAT IS AND IS NOT AVAILABLE. Nothing in this system PERSISTS an
 * impression-share number: campaign_grid_metrics has no such column, which is
 * what NEEDS.impressionShare says and why this group rendered "not wired". What
 * the growth run does persist is its own READING of the auction — and that is a
 * different thing that happens to answer the same question.
 *
 * Three sources, in descending order of how structured they are:
 *
 *   1. performance_summary.primary_constraint === 'rank'. A closed vocabulary
 *      the schema pins. This is the analysis stating outright that what limits
 *      the campaign is being outranked — the only competitive VERDICT anything
 *      here stores.
 *   2. scoring.expected_lift.metric === 'impression_share' (card.lm). Also a
 *      closed vocabulary, and it means the suggestion is being sold on the
 *      auction share it wins back.
 *   3. A suggestion whose evidence CITES a named impression-share field. The
 *      brief requires the model to name the field it is quoting, so matching
 *      `search_rank_lost_impression_share` in evidence text is matching a
 *      citation — not scraping prose for anything percentage-shaped.
 *
 * WHY THE FIELD NAMES AND NOT THE WORDS. Matching "competitor" or "auction"
 * would catch every mechanism sentence that reaches for the metaphor, and the
 * mechanism sentence is where the model writes freely. The snake_case field
 * names are quotations from a data payload; the model does not produce them by
 * accident, and no other kind of finding contains them.
 */

/** The impression-share fields the briefs require an evidence line to name. */
const IS_FIELDS = [
  ['search_budget_lost_impression_share', 'budget-lost IS'],
  ['search_rank_lost_impression_share', 'rank-lost IS'],
  ['search_impression_share', 'IS'],
];

/**
 * The percentage a cited field is quoted AT, or null.
 *
 * Anchored to the field name and bounded to what can follow it before the
 * number — "is", "of", "at", "sits at", a colon — so it reads a figure out of
 * "search_budget_lost_impression_share is 31.4%" and reads NOTHING out of a
 * sentence that names the field and then quotes a CPA. A percentage lifted from
 * the wrong clause is worse than no percentage: it is a measurement-shaped
 * number that measures something else.
 */
function citedPct(text, field) {
  const m = new RegExp(
    `${field}[^.%]{0,24}?(\\d{1,3}(?:\\.\\d+)?)\\s*%`,
    'i',
  ).exec(String(text || ''));
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

/**
 * The competitive read for ONE campaign, or null when the analysis produced
 * none.
 *
 * Null is the answer the user asked for and the answer this file's rules
 * already require: a campaign whose runs said nothing about the auction gets an
 * empty cell, not a zero and not an "unknown" badge. Rendering 0% impression
 * share for a campaign nobody measured would be the same lie as rendering 0
 * conversions for a campaign nobody pulled.
 */
export function competitiveFor(cards = [], perfSummary = null) {
  const signals = [];

  // 1 — the run's own constraint verdict.
  if (String(perfSummary?.constraint || '').toLowerCase() === 'rank') {
    signals.push({
      kind: 'rank-constrained',
      label: 'Rank-constrained',
      // The evidence line the schema requires alongside the verdict.
      detail: perfSummary.constraintEvidence || perfSummary.headline || null,
      pct: citedPct(perfSummary.constraintEvidence, 'search_rank_lost_impression_share'),
      field: null,
    });
  }

  // 2 and 3 — per suggestion.
  for (const card of cards) {
    const text = `${card.ev || ''} ${card.me || ''}`;
    for (const [field, short] of IS_FIELDS) {
      if (!text.toLowerCase().includes(field)) continue;
      signals.push({
        kind: 'is-cited',
        label: short,
        detail: card.ev || null,
        pct: citedPct(text, field),
        field,
      });
      // ONE signal per card. A suggestion that cites two impression-share
      // fields is still one finding about the auction, and the cell counts
      // findings — letting it emit two would inflate the ×n badge into a count
      // of sentences rather than a count of things the run found.
      break;
    }
    if (String(card.lm || '').toLowerCase() === 'impression_share') {
      signals.push({
        kind: 'lift-is',
        label: 'IS lift',
        detail: card.head || card.plain || null,
        // The lift's own percentage is a PROJECTION, not a measured share.
        // Carried separately so the cell never prints a forecast where a
        // measurement belongs.
        pct: null,
        projPct: card.lp ?? null,
        field: null,
      });
    }
  }

  if (!signals.length) return null;

  // The strongest measured figure the campaign has, for the cell's headline
  // number. Rank-lost outranks budget-lost as a COMPETITIVE reading: budget-lost
  // share is share we declined to buy, which is a spending decision; rank-lost
  // share is share somebody else took.
  const measured = signals.filter((s) => s.pct !== null);
  const rankLost = measured.find((s) => s.field === 'search_rank_lost_impression_share')
    || measured.find((s) => s.kind === 'rank-constrained');
  const head = rankLost || measured[0] || null;

  return {
    n: signals.length,
    // True when the growth run named rank as THE constraint — the strongest
    // competitive claim available, and what the cell highlights.
    rankConstrained: signals.some((s) => s.kind === 'rank-constrained'),
    pct: head ? head.pct : null,
    label: head ? head.label : signals[0].label,
    // Every distinct evidence line behind the cell, for its title text. The
    // reader is owed the citation, because the cell is quoting a run.
    evidence: [...new Set(signals.map((s) => s.detail).filter(Boolean))],
  };
}

/* ── Quality ───────────────────────────────────────────────────────────────
 *
 * NOT GOOGLE'S QUALITY SCORE. Google's is 1-10, per keyword, and nothing here
 * stores it — that is what this group's `needs` line said and it is still true.
 * This is a different measurement with a different name: how much of what this
 * campaign is built out of can actually serve, right now, according to its own
 * most recent diagnostic run.
 *
 * EVERY INPUT IS A FIELD THE DIAGNOSTIC SCHEMA PINS, and every one is a closed
 * vocabulary or a count:
 *
 *   campaign_serving.status          SERVING | LIMITED | NOT_SERVING
 *   measurement_check.verdict        OK | AT_RISK | BLOCKED
 *   campaign_health.*_checked        the denominator the run itself counted
 *   campaign_health.*_blocked        entities that cannot deliver
 *   campaign_health.ads_disapproved  SEM
 *   campaign_health.*_weak_strength  PMax (Google's own POOR/AVERAGE ad strength)
 *   blocking_issues[].severity       HIGH | MEDIUM | LOW
 *
 * The weights below are a JUDGEMENT and are stated as one — they are not
 * derived from anything, and the breakdown is carried on the result so the cell
 * can show its arithmetic rather than asking to be trusted. What is NOT a
 * judgement is the inputs: no term here is a proxy, an estimate or a model
 * output. A campaign scores badly because a run counted blocked entities, not
 * because something inferred that it might have some.
 *
 * DEDUCTIVE, from 100. A campaign with nothing wrong scores 100, and every
 * deduction names a finding a reader can go and look at. The alternative — an
 * additive score out of weighted positives — produces a number nobody can
 * attribute to anything.
 *
 * NO DIAGNOSTIC RUN MEANS NO SCORE. Not 100, and not 0. "Nothing has checked
 * this campaign" and "this campaign is fine" are different claims, and the
 * whole point of this file is not to collapse them.
 */

/** What each deduction is worth, and why it is worth that. */
const QUALITY_WEIGHTS = {
  // The campaign is off. Nothing else about it matters as much, so this alone
  // can take a campaign out of the passing band.
  notServing: 40,
  limited: 15,
  // The bidder is optimizing toward a signal it is not receiving. Every
  // conversion-based lever downstream is compromised by it.
  measurementBlocked: 25,
  measurementAtRisk: 10,
  // Scaled by the SHARE of checked entities that cannot deliver, not by their
  // count: 3 blocked ad groups out of 4 is a broken campaign and 3 out of 300
  // is a Tuesday.
  blockedShare: 30,
  // Disapproved ads (SEM) and weak ad strength (PMax) — the creative side of
  // the same question. Lighter because weak strength limits reach rather than
  // stopping delivery.
  weakShare: 15,
  // Named blockers the run raised on the campaign itself.
  highBlocker: 5,
  highBlockerCap: 15,
};

const BANDS = [
  [80, 'good', 'Good'],
  [60, 'fair', 'Fair'],
  [0, 'poor', 'Poor'],
];

/** `n/d` as a 0..1 share, or 0 when the run counted no denominator. */
function share(n, d) {
  const top = Number(n);
  const bottom = Number(d);
  if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= 0) return 0;
  return Math.max(0, Math.min(1, top / bottom));
}

/**
 * A 0-100 delivery-quality score for one campaign, or null with no diagnostic.
 *
 * `diagnosis` is the shape routes/v2/performance.js builds for the Diagnostics
 * tab — health / serving / measurement / entities / blockers — read here rather
 * than re-derived, so the score and that tab cannot disagree about the same run.
 */
export function qualityFor(diagnosis, diagnosedAt = null) {
  if (!diagnosis) return null;

  const h = diagnosis.health || {};
  const serving = String(diagnosis.serving?.status || '').toUpperCase();
  const measurement = String(diagnosis.measurement?.verdict || '').toUpperCase();
  const deductions = [];
  const take = (points, label) => {
    if (points > 0) deductions.push({ points: Math.round(points), label });
  };

  if (serving === 'NOT_SERVING') take(QUALITY_WEIGHTS.notServing, 'Campaign is not serving');
  else if (serving === 'LIMITED') take(QUALITY_WEIGHTS.limited, 'Serving is limited');

  if (measurement === 'BLOCKED') take(QUALITY_WEIGHTS.measurementBlocked, 'Conversion tracking is blocked');
  else if (measurement === 'AT_RISK') take(QUALITY_WEIGHTS.measurementAtRisk, 'Conversion tracking is at risk');

  // SEM counts ad groups, PMax counts asset groups, DV360 counts line items.
  // All three are "the things this campaign is built out of", and the score does
  // not care which word the platform uses — but the label does, so the reader
  // sees their own vocabulary.
  //
  // DV360 was missing, and silently. dv360_diagnostic_optimizer_launcher emits
  // campaign_health.line_items_checked / line_items_blocked; this read only the
  // ad_groups_* and asset_groups_* pairs, so `checked` and `blocked` came back
  // undefined, share() turned that into 0, and the blockedShare deduction — 30
  // points, the largest one here after "not serving" — contributed NOTHING. A
  // DV360 campaign with every line item blocked scored the same as a healthy
  // one. Nothing errored; the biggest signal just weighed zero.
  const isPmax = h.asset_groups_checked != null || h.asset_groups_blocked != null;
  const isDv360 = !isPmax
    && (h.line_items_checked != null || h.line_items_blocked != null);
  const unit = isPmax ? 'asset groups' : isDv360 ? 'line items' : 'ad groups';
  const checked = isPmax ? h.asset_groups_checked
    : isDv360 ? h.line_items_checked : h.ad_groups_checked;
  const blocked = isPmax ? h.asset_groups_blocked
    : isDv360 ? h.line_items_blocked : h.ad_groups_blocked;
  const blockedShare = share(blocked, checked);
  if (blockedShare > 0) {
    take(
      QUALITY_WEIGHTS.blockedShare * blockedShare,
      `${blocked} of ${checked} ${unit} cannot deliver`,
    );
  }

  const weak = isPmax ? h.asset_groups_weak_strength : h.ads_disapproved;
  const weakShare = share(weak, checked);
  if (weakShare > 0) {
    take(
      QUALITY_WEIGHTS.weakShare * weakShare,
      isPmax
        ? `${weak} of ${checked} asset groups have weak ad strength`
        : `${weak} disapproved ads across ${checked} ad groups`,
    );
  }

  const high = (diagnosis.blockers || []).filter(
    (b) => String(b?.severity || '').toUpperCase() === 'HIGH',
  ).length;
  if (high > 0) {
    take(
      Math.min(high * QUALITY_WEIGHTS.highBlocker, QUALITY_WEIGHTS.highBlockerCap),
      `${high} high-severity blocking ${high === 1 ? 'issue' : 'issues'}`,
    );
  }

  const lost = deductions.reduce((n, d) => n + d.points, 0);
  const score = Math.max(0, Math.min(100, 100 - lost));
  const band = BANDS.find(([floor]) => score >= floor);

  return {
    score,
    band: band[1],
    bandLabel: band[2],
    // The run's own headline verdict, carried beside the score rather than
    // folded into it. HEALTHY/DEGRADED/STALLED is the diagnostic's summary
    // judgement and the score is an arithmetic of its parts; showing both lets
    // a reader see when they disagree, which is a fact about the run.
    verdict: diagnosis.health?.verdict || null,
    deductions,
    // WHEN this read was taken. "At this moment" is only true of the moment the
    // run completed, and a score with no date silently claims to be live.
    at: diagnosedAt,
  };
}

/**
 * One table row per campaign, with the derived columns the design's Performance
 * group shows. Deltas are omitted rather than faked — see the file header.
 *
 * `flags` come from the optimizer's own findings, not from a threshold invented
 * here: a "capped" badge means the pacing agent said so about this campaign.
 * That keeps the badge a report of something measured.
 *
 * `runsByCampaign` is what usePerformanceRuns collected per campaign — the
 * diagnostic's own diagnosis and the growth run's constraint verdict. It is what
 * the Competitive and Quality groups are built from, and a campaign missing from
 * it simply has no run behind it: both cells come back null and both render
 * empty, which is the only honest thing to put where nothing was measured.
 */
export function campaignRows(campaigns, cards = [], runsByCampaign = {}) {
  const byCampaign = new Map();
  for (const card of cards) {
    if (!card.camp) continue;
    const list = byCampaign.get(card.camp) || [];
    list.push(card);
    byCampaign.set(card.camp, list);
  }

  return campaigns.map((c) => {
    const cost = num(c.spend);
    const impressions = num(c.impressions);
    const clicks = num(c.clicks);
    const conversions = num(c.conversions);
    const own = byCampaign.get(c.id) || [];
    const runs = runsByCampaign[c.id] || null;
    const flags = [];
    if (own.some((k) => k.route === 'pacing')) flags.push({ kind: 'cap2', label: 'pacing' });
    if (own.some((k) => k.route === 'diagnostics')) flags.push({ kind: 'trk', label: 'diagnostic' });
    return {
      id: c.id,
      name: c.name,
      type: c.destination || null,
      status: c.platform_status || c.status || null,
      cost,
      impressions,
      clicks,
      conversions,
      // Derived, not stored — and derived from THIS row's own parts so a row can
      // never disagree with itself.
      ctr: num(c.ctr) !== null ? num(c.ctr) : ratio(clicks, impressions),
      cpc: ratio(cost, clicks),
      cpa: ratio(cost, conversions),
      currency: c.currency_code || null,
      flightEnd: c.flight_end_date || null,
      // Null when the runs said nothing about the auction, and null when no
      // diagnostic has looked at this campaign. Both render as an empty cell.
      competitive: competitiveFor(own, runs?.perfSummary || null),
      quality: qualityFor(runs?.diagnosis || null, runs?.diagnosedAt || null),
      flags,
      findings: own.length,
      // null with no date range picked; true/false once applyWindow has run, so
      // the table can mark the rows a range could not be computed for instead of
      // leaving them looking like campaigns that spent nothing.
      windowed: c.windowed ?? null,
    };
  });
}

/** Highest spend first. Nulls sort last — unmeasured is not "lowest". */
export function topSpenders(rows, limit = 3) {
  return [...rows]
    .filter((r) => r.cost !== null)
    .sort((a, b) => b.cost - a.cost)
    .slice(0, limit);
}

/**
 * CPA deterioration — the campaigns paying more per conversion now than they
 * were, from the daily rows in the last pull's artifact.
 *
 * WHERE THE PRIOR PERIOD COMES FROM. Not from campaign_grid_metrics, which has
 * no date dimension: the server splits the pull's own daily rows into two equal
 * halves (`/campaign-deltas`), so "was" is the older half and "now" is the
 * newer. That is the same boundary the trend chart's comparison overlay draws,
 * and `daysPerPeriod` says how long each half actually is — this is NOT the
 * design's 30-vs-30, and the view prints the real number rather than that copy.
 *
 * THE GATES, and why each one is here rather than left to the reader:
 *
 *   • A CPA on BOTH sides. A campaign with no conversions in a half has no
 *     cost-per-conversion there — not an infinite one, and not zero. A campaign
 *     that has stopped converting entirely is a real and worse problem, and it
 *     is the "Spending, not converting" list's to report, with the spend as the
 *     number instead of a CPA that does not exist.
 *   • MIN_CONVERSIONS in both halves. Going from 1 conversion to 2 moves CPA by
 *     50% and means nothing; on a half-window of a few days, small counts are
 *     most of what is there.
 *   • A MIN_WORSENING floor. CPA moves every week. Below this the list is noise
 *     with a red arrow on it, which is worse than an empty list.
 *
 * RANKED BY MONEY, NOT BY PERCENTAGE. The extra cost is `conversions × ΔCPA` —
 * what the same conversions cost at the new rate over what they would have cost
 * at the old one. Ranking by percentage puts a 300% rise on a campaign that
 * spent $40 above a 25% rise on one that spent $80,000, and the second is the
 * one worth someone's afternoon.
 */
export const CPA_MIN_CONVERSIONS = 3;
export const CPA_MIN_WORSENING = 0.2;

export function cpaDeterioration(deltas, rows, limit = 3) {
  if (!deltas || !deltas.daysPerPeriod) {
    return unavailable(
      'no two periods to compare',
      deltas?.reason || NEEDS.series,
    );
  }
  const byId = deltas.campaigns || {};
  const out = [];
  for (const r of rows) {
    const d = byId[r.id];
    if (!d) continue;
    const was = d.previous?.cpa ?? null;
    const now = d.current?.cpa ?? null;
    if (was === null || now === null || was <= 0) continue;
    if (d.previous.conversions < CPA_MIN_CONVERSIONS) continue;
    if (d.current.conversions < CPA_MIN_CONVERSIONS) continue;
    const change = (now - was) / was;
    if (change < CPA_MIN_WORSENING) continue;
    out.push({
      id: r.id,
      name: r.name,
      currency: r.currency,
      ...(deltas.independentPeriods ? { period: { daysPerPeriod: d.daysPerPeriod, priorRange: d.priorRange, currentRange: d.currentRange } } : {}),
      was,
      now,
      change,
      conversions: d.current.conversions,
      // Exactly `d.current.cost - d.current.conversions * was`, since a period's
      // cost is its CPA times its conversions. Written the short way because the
      // long way invites the reader to check whether the two agree.
      excess: d.current.conversions * (now - was),
    });
  }
  return out.sort((a, b) => b.excess - a.excess).slice(0, limit);
}

/**
 * Top movers — the campaigns whose conversions changed most between the two
 * halves of the pull's own days.
 *
 * SAME COMPARISON AS CPA DETERIORATION, deliberately: both read
 * /campaign-deltas, which halves the window's date list with the same
 * halveDates the chart's overlay uses. Three reads on one screen disagreeing
 * about which day "before" ends on would be its own defect, and building a
 * second definition here was the alternative.
 *
 * RANKED BY THE ABSOLUTE CHANGE, not the percentage — the volume gate the
 * design asks for ("a volume threshold so noise cannot rank"), reached by
 * choosing the right key rather than by adding a floor on top. A campaign going
 * from 2 conversions to 4 has doubled and moved by two; one going from 400 to
 * 500 has grown a quarter and moved by a hundred. The second is the thing that
 * happened to the account, and ranking by rate puts the first above it every
 * time.
 *
 * MIN_DELTA is a floor all the same, and a small one, because Google Ads
 * reports conversions as a DOUBLE: fractional attribution makes a 0.4-conversion
 * move possible, and a list of them would render as "+0" beside a campaign name.
 * One whole conversion is the smallest movement this list can state honestly.
 *
 * `pct` is null when the earlier half was zero. There is no percentage change
 * from nothing, and +100% or infinity are numbers nobody measured — those
 * campaigns carry `isNew`, which the view renders as "started" instead.
 */
export const MOVERS_MIN_DELTA = 1;

export function topMovers(deltas, rows, { direction = 'up', limit = 3 } = {}) {
  if (!deltas || !deltas.daysPerPeriod) {
    return unavailable('no two periods to compare', deltas?.reason || NEEDS.series);
  }
  const byId = deltas.campaigns || {};
  const up = direction === 'up';
  const out = [];
  for (const r of rows) {
    const d = byId[r.id];
    if (!d) continue;
    const was = num(d.previous?.conversions);
    const now = num(d.current?.conversions);
    if (deltas.independentPeriods && (was === null || now === null)) continue;
    // A half the campaign has no rows in is a measured zero — see
    // splitCampaignPeriods. Only a campaign absent from the response entirely
    // (no daily rows at all) is skipped, and that happened above.
    const previous = was === null ? 0 : was;
    const current = now === null ? 0 : now;
    const delta = current - previous;
    if (up ? delta < MOVERS_MIN_DELTA : delta > -MOVERS_MIN_DELTA) continue;
    out.push({
      id: r.id,
      name: r.name,
      currency: r.currency,
      ...(deltas.independentPeriods ? { period: { daysPerPeriod: d.daysPerPeriod, priorRange: d.priorRange, currentRange: d.currentRange } } : {}),
      previous,
      current,
      delta,
      pct: previous > 0 ? delta / previous : null,
      // Delivered now and on none of the earlier half's days. A real move, and
      // not a percentage.
      isNew: (d.previous?.days ?? 0) === 0 && (d.current?.days ?? 0) > 0,
    });
  }
  return out
    .sort((a, b) => (up ? b.delta - a.delta : a.delta - b.delta))
    .slice(0, limit);
}

/** "+12" / "−4", with the sign a reader expects rather than a bare hyphen. */
export function signed(n, decimals = 0) {
  if (n === null || n === undefined) return '—';
  const rounded = Number(n.toFixed(decimals));
  const body = Math.abs(rounded).toLocaleString(undefined, {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
  if (rounded > 0) return `+${body}`;
  if (rounded < 0) return `\u2212${body}`;
  return body;
}

/**
 * Google's open-ended flight sentinel. A campaign carrying it has no end date,
 * so it can never be "ending soon" — without this it would read as ending in
 * roughly four thousand days, or, worse, sort to the top of a soonest-first
 * list on some other reading.
 */
const OPEN_ENDED = '2037-12-30';

/** Days until a flight ends, or null when there is no usable end date. */
export function daysToFlightEnd(flightEnd, today = new Date()) {
  if (!flightEnd) return null;
  const iso = String(flightEnd).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || iso === OPEN_ENDED) return null;
  const end = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(end)) return null;
  const t = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((end - t) / 86400000);
}

/**
 * The health strip.
 *
 * Counts findings the agents actually produced, plus one thing that IS
 * derivable from stored data — flights about to end. The design's exact
 * wording ("2 blocking · 3 budget-limited") maps onto the two card routes that
 * mean those things; nothing here invents a severity the run did not assign.
 */
export function health(cards, rows, today = new Date(), endingWithinDays = 7) {
  const diagnostics = cards.filter((c) => c.route === 'diagnostics').length;
  const pacing = cards.filter((c) => c.route === 'pacing').length;
  const ending = rows.filter((r) => {
    const d = daysToFlightEnd(r.flightEnd, today);
    return d !== null && d >= 0 && d <= endingWithinDays;
  });
  return {
    diagnostics,
    pacing,
    ending: ending.length,
    endingSoonest: ending.length
      ? ending
        .map((r) => ({ name: r.name, days: daysToFlightEnd(r.flightEnd, today) }))
        .sort((a, b) => a.days - b.days)[0]
      : null,
    clear: diagnostics === 0 && pacing === 0 && ending.length === 0,
  };
}

/* ── formatting ─────────────────────────────────────────────────────────── */

const CURRENCY_SYMBOL = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', AUD: 'A$', CAD: 'C$', AED: 'AED ',
};

export function money(v, currency = null, decimals = 0) {
  if (v === null || v === undefined || !/^[A-Z]{3}$/.test(currency || '')) return '—';
  const sym = CURRENCY_SYMBOL[currency] || `${currency} `;
  return sym + v.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmt(value, format, currency = null) {
  if (value === null || value === undefined) return '—';
  switch (format) {
    case 'currency': return money(value, currency, 0);
    case 'currency2': return money(value, currency, 2);
    case 'int': return Math.round(value).toLocaleString();
    // Stored as a fraction by the grid pull; shown as a percentage.
    case 'pct': return `${(value * 100).toFixed(1)}%`;
    case 'ratio': return value.toFixed(2);
    default: return String(value);
  }
}
